import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { Redirect, withRouter } from "react-router-dom";
import { change, formValueSelector, reduxForm } from "redux-form";
import { Button } from "cxa-components";
import { StringField } from "cxa-form-field";
import { injectIntl, intlShape } from "react-intl";
import messages from "components/messagesDescription";
import { SelectField } from "../common/form-field";
import {
  getDayOptions,
  getDaysInMonth,
  getMonthOptions,
  getYearOptions,
} from "../../utils/dateOptions";
import { connect } from "react-redux";

const validateRequired = (value, values, { intl: { formatMessage } }) =>
  value ? null : formatMessage(messages.requiredDescription);

const selector = formValueSelector("forgotPasswordForm");

const ForgotPasswordForm = ({
  initialValues,
  handleSubmit,
  submitting,
  submitted,
  response,
  error,
  onCancel,
  loginLabel,
  intl,
  history,
  dateOfBirth,
  day,
  month,
  year,
  loginId,
  dispatch
}) => {

  useEffect(() => {
    if (response && response.body && response.body.resetPasswordURL) {
      history.push(response.body.resetPasswordURL);
    }
  }, [response, history]);

  const companyId = !initialValues.companyId && (
    <StringField
      intl={intl}
      label={intl.formatMessage(messages.companyIdDescription)}
      name="companyId"
      // name="clientName"
      autoCorrect="off"
      autoCapitalize="none"
      required
      validate={validateRequired}
      boxed
    />
  );

  let redirector = null;

  if (submitting) {
    // msgDescriptor = messages.submittingDescription;
  } else if (submitted || error) {
    // redirect back to logout for error or submitted
    // so there is no way for attacker to guess
    // employee id from frontend
    redirector = <Redirect to="/logout" />;
  }
  const loginIdLabel = loginLabel
    ? intl.formatMessage({ id: loginLabel, defaultMessage: loginLabel })
    : intl.formatMessage(messages.loginIdDescription);

  const yearOptions = getYearOptions(intl.formatMessage, 100);

  const currentDayOptions = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDate = now.getDate();

    if (!month) return getDayOptions(intl.formatMessage); // Default 1-31 if no month selected

    // Special cases when year is not selected
    if (!year) {
      if (month === "February") {
        return getDayOptions(intl.formatMessage, 28); // Force 28 days for February without year
      }
      if (["April", "June", "September", "November"].includes(month)) {
        return getDayOptions(intl.formatMessage, 30); // 30 days for these months
      }
    }

    if (month && year) {
      const selectedYear = parseInt(year, 10);
      const monthIndex = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ].indexOf(month);

      let maxDays = getDaysInMonth(month, year);

      if (selectedYear === currentYear && monthIndex === currentMonth) {
        maxDays = Math.min(maxDays, currentDate);
      }

      return getDayOptions(intl.formatMessage, maxDays);
    }

    // Normal case with year selected
    const maxDays = getDaysInMonth(month, year || null);
    return getDayOptions(intl.formatMessage, maxDays);
  };

  const currentMonthOptions = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11 (Jan-Dec)

    if (year) {
      const selectedYear = parseInt(year, 10);

      if (selectedYear === currentYear || selectedYear === 2025) {
        return getMonthOptions(intl.formatMessage, currentMonth + 1);
      }
    }

    return getMonthOptions(intl.formatMessage);
  };

  useEffect(() => {
    const isFilled = (value) => typeof value === "string" && value.trim() !== "";
    
    if (isFilled(day) && isFilled(month)) {
      const selectedDay = parseInt(day, 10);
      let shouldReset = false;

      // 1. Validate day against month (including special cases without year)
      const maxDays = getDaysInMonth(month, year || null);
      if (selectedDay > maxDays) {
        shouldReset = true;
      }

      // 2. Special case: February without year
      if (!year && month === "February" && (day === "29" || day === "30" || day === "31")) {
        shouldReset = true;
      }

      // 3. Special case: April without year
      if (!year && month === "April" && day === "31") {
        shouldReset = true;
      }

      if (isFilled(year) && isFilled(month) && isFilled(day)) {
        const selectedDate = new Date(
          parseInt(year),
          [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ].indexOf(month),
          parseInt(day, 10)
        );

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate > today) {
          shouldReset = true;
        }
      }

      if (shouldReset) {
        dispatch(change("forgotPasswordForm", "day", ""));
      }
    }
  }, [day, month, year]);  

  return (
    <form className="cxa-c-form" onSubmit={handleSubmit}>
      <fieldset>
        {companyId}
        <StringField
          intl={intl}
          label={loginIdLabel}
          name="loginId"
          autoCorrect="off"
          autoCapitalize="none"
          required
          validate={validateRequired}
          boxed
          size="10"
        />
        {dateOfBirth && (
          <div>
            <div className="field">
              <div className="field__label-wrapper">
                <strong>
                  <label className="field__label" htmlFor="loginId">
                    <span aria-label="Required" className="field__asterisk">
                      *
                    </span>
                    {intl.formatMessage(messages.brithDateDescription)}
                  </label>
                </strong>
              </div>
            </div>
            <div className="container-dob">
              <div className="dob-field">
                <SelectField
                  name="year"
                  value={year}
                  items={yearOptions}
                  validate={validateRequired}
                  boxed
                  required
                />
              </div>
              <div className="dob-field">
                <SelectField
                  name="month"
                  value={month}
                  items={currentMonthOptions()}
                  validate={validateRequired}
                  boxed
                />
              </div>
              <div className="dob-field">
                <SelectField
                  name="day"
                  value={day}
                  items={currentDayOptions()}
                  validate={validateRequired}
                  boxed
                />
              </div>
            </div>
          </div>
        )}
      </fieldset>
      {redirector}
      <div className="form__action">
        <Button primary submitting={submitting} disabled={!day || !month || !year || !loginId}>
          {intl.formatMessage(messages.resetPasswordDescription)}
        </Button>
        <Button primary type="button" disabled={submitting} onClick={onCancel}>
          {intl.formatMessage(messages.cancelDescription)}
        </Button>
      </div>
    </form>
  );
};

ForgotPasswordForm.propTypes = {
  initialValues: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  submitted: PropTypes.bool,
  response: PropTypes.object,
  error: PropTypes.string,
  onCancel: PropTypes.func,
  loginLabel: PropTypes.string,
  history: PropTypes.object,
  dateOfBirth: PropTypes.bool,
  intl: intlShape.isRequired,
  day: PropTypes.string,
  month: PropTypes.string,
  year: PropTypes.string,
  loginId: PropTypes.string,
  dispatch: PropTypes.func,
};

const mapStateToProps = (state) => {
  return {
    day: selector(state, "day"),
    month: selector(state, "month"),
    year: selector(state, "year"),
    loginId: selector(state, "loginId"),
  };
};

export default withRouter(
  injectIntl(
    connect(mapStateToProps)(
      reduxForm({
        form: "forgotPasswordForm",
        destroyOnUnmount: false,
      })(ForgotPasswordForm)
    )
  )
);



// WEBPACK FOOTER //
// ./components/password/ForgotPasswordForm.js